/* eslint-disable @typescript-eslint/naming-convention */
import React, { ChangeEvent, FunctionComponent, useRef, useState } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import useDispatch from 'hooks/useDispatch'
import { createVoucher } from 'actions/voucherActions'
import { mediaMax } from 'styles/media'

import TextareaField from 'components/hookForm/TextareaField'
import TextField from 'components/hookForm/TextField'
import Button from 'components/display/Button'
import Overlay from 'components/display/Overlay'
import Spinner from 'components/display/Spinner'
import SelectField from 'components/hookForm/SelectField'
import CurrencyField from 'components/hookForm/CurrencyField'
import ErrorMessage from 'components/hookForm/ErrorMessage'
import { HintWrapper, InputGroup, Label } from 'components/form'
import { formatPrice, formatPriceAsNumber } from 'components/utils/pricing'
import { Currency, countryForCurrency } from 'utils/currency'

const MESSAGE_MAX_LENGTH = 300

export const trimNewLines = (value: string) => {
  if (!value) {
    return value
  }
  return value.replace(/(\r\n\t|\n|\r\t)/gm, '')
}

const Form = styled.form`
  max-width: 744px;
  margin-bottom: 64px;
`

const CustomAmountInputAndCurrencySignWrapper = styled.div`
  display: flex;
  /* justify-content: center; */
  align-items: center;

  /* padding: 0 8px; */

  input {
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-xs']?.['font-size']?.value};
    font-weight: 400;
    margin: 0;
    padding: 0;
    border: 1px solid transparent;
    background-color: transparent;

    &:focus {
      border: 1px solid transparent;
    }

    &::-webkit-input-placeholder {
      /* Chrome/Opera/Safari */
      color: #999;
    }
    &::-moz-placeholder {
      /* Firefox 19+ */
      color: #999;
    }
    &:-ms-input-placeholder {
      /* IE 10+ */
      color: #999;
    }
    &:-moz-placeholder {
      /* Firefox 18- */
      color: #999;
    }
  }
`

const HorizontalHiddenRadioLabel = styled.label<{ selected: boolean }>`
  input[type='radio'] {
    position: absolute;
    visibility: hidden;
  }

  width: 140px;
  ${mediaMax.md`
    width: 100%;
  `}
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;

  cursor: pointer;
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  font-weight: 400;
  color: #1d1d1d;
  padding: 16px;

  transition: background-color 300ms ease-out;
  transition: border 300ms ease-out;

  ${({ selected, theme }) =>
    selected &&
    `
    border-color: ${theme.brand.jungle};
    color: ${theme.brand.jungle};
    background: rgba(12, 103, 118, 0.1);

    input {
      color: ${theme.brand.jungle};
    }
  `}
`

const InputGroupHiddenRadios = styled.div`
  display: flex;
  margin-top: 8px;

  ${HorizontalHiddenRadioLabel} {
    position: relative;
    flex-grow: 1;
    margin-bottom: 16px;

    &:not(:last-child) {
      margin-right: 16px;
    }
  }
`

const TotalLabel = styled.span`
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
`

const TotalAmount = styled.span`
  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  margin-left: auto;
`

const TotalRow = styled.div`
  display: flex;
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-lg']?.['font-size']?.value};
  font-weight: 500;
  margin-bottom: 30px;
`

const Disclaimer = styled.p`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xxs']?.['font-size']?.value};
  color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};
`

const SubmitButton = styled(Button).attrs({
  uppercase: false,
})`
  ${mediaMax.md`
    width: 100%;
  `}
`

const amounts = {
  '100': 100,
  '200': 200,
  '300': 300,
  custom: 'custom',
} as const

type VoucherFormFields = {
  recipient_name: string
  from_name: string
  currency: Currency
  custom_amount: number
  message: string
}

type VoucherCreateProps = {
  updating: boolean
  listing_id?: number
  story_id?: number
}

const VoucherCreate: FunctionComponent<VoucherCreateProps> = ({
  updating,
  listing_id,
  story_id,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('spelling')

  const [selectedAmountKey, setSelectedAmountKey] = useState<
    keyof typeof amounts
  >('100')

  const customInputRef = useRef<HTMLInputElement>()

  const form = useForm<VoucherFormFields>({
    mode: 'onTouched',
  })

  const selectedCurrency = useWatch({
    control: form.control,
    name: 'currency',
  })

  const customAmount = useWatch({
    control: form.control,
    name: 'custom_amount',
  })

  const handleSelectedAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setSelectedAmountKey(value as keyof typeof amounts)
    if (value !== 'custom_amount') {
      form.setError('custom_amount', undefined)
    }
  }

  const { isSubmitting } = form.formState

  const onSubmit = (formValues: VoucherFormFields) => {
    const {
      currency,
      custom_amount,
      from_name,
      message,
      recipient_name,
    } = formValues

    const selectedAmount = amounts[selectedAmountKey]
    const amount = selectedAmount === 'custom' ? custom_amount : selectedAmount

    return dispatch(
      createVoucher({
        listing_id,
        story_id,
        from_name,
        recipient_name,
        amount,
        message,
        currency,
      })
    )
  }

  const currencySymbol = '$'
  const currencyCountry = countryForCurrency(selectedCurrency)

  const selectedAmount = amounts[selectedAmountKey]
  const amount = selectedAmount === 'custom' ? customAmount : selectedAmount

  const customAmountValidationMessage = `Please enter an amount between ${currencySymbol}50 and ${currencySymbol}2,500`

  return (
    <FormProvider {...form}>
      <Form
        className="qa-voucher-create"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <InputGroup>
          <TextField
            name="recipient_name"
            label="Who is it for?"
            placeholder="Recipient Name"
            registerOptions={{
              minLength: {
                value: 1,
                message: 'Must be at least 1 character long',
              },
              maxLength: {
                value: 60,
                message: 'Cannot be longer than 60 characters',
              },
              required: {
                value: true,
                message: "Please enter the recipient's name",
              },
            }}
          />
        </InputGroup>

        <InputGroup>
          <SelectField
            name="currency"
            placeholder="Please select"
            label={<span>Where will they be {t('traveling')}?</span>}
            registerOptions={{
              required: {
                value: true,
                message: 'Please select an option',
              },
            }}
          >
            <option value="AUD">Australia (AUD)</option>
            <option value="NZD">New Zealand (NZD)</option>
            <option value="USD">United States (USD)</option>
          </SelectField>
          <HintWrapper noLeftPadding>
            This gift voucher can only be used in the country you select.
          </HintWrapper>
        </InputGroup>

        <InputGroup>
          <TextField
            name="from_name"
            label="Who is it from?"
            placeholder="Sender Name"
            registerOptions={{
              minLength: {
                value: 1,
                message: 'Must be at least 1 character long',
              },
              maxLength: {
                value: 60,
                message: 'Cannot be longer than 60 characters',
              },
              required: {
                value: true,
                message: "Please enter the sender's name",
              },
            }}
          />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="amount">
            <span>Choose an amount</span>
          </Label>
          <InputGroupHiddenRadios
            role="radiogroup"
            aria-label="Choose an amount"
          >
            <HorizontalHiddenRadioLabel
              selected={selectedAmountKey === '100'}
              id="amount-100"
            >
              <input
                id="amount_100"
                name="amount"
                type="radio"
                value="100"
                aria-label="100"
                checked={selectedAmountKey === '100'}
                onChange={handleSelectedAmountChange}
              />
              <span>{currencySymbol}100</span>
            </HorizontalHiddenRadioLabel>
            <HorizontalHiddenRadioLabel
              selected={selectedAmountKey === '200'}
              id="amount-200"
            >
              <input
                id="amount_200"
                name="amount"
                type="radio"
                value="200"
                aria-label="200"
                checked={selectedAmountKey === '200'}
                onChange={handleSelectedAmountChange}
              />
              <span>{currencySymbol}200</span>
            </HorizontalHiddenRadioLabel>
            <HorizontalHiddenRadioLabel
              selected={selectedAmountKey === '300'}
              id="amount-300"
            >
              <input
                id="amount_300"
                name="amount"
                type="radio"
                value="300"
                aria-label="300"
                checked={selectedAmountKey === '300'}
                onChange={handleSelectedAmountChange}
              />
              <span>{currencySymbol}300</span>
            </HorizontalHiddenRadioLabel>
          </InputGroupHiddenRadios>

          <HorizontalHiddenRadioLabel
            selected={selectedAmountKey === 'custom'}
            id="amount-custom"
            onClick={() => {
              customInputRef.current?.focus()
            }}
            style={{ width: '100%', justifyContent: 'flex-start' }}
            aria-label="Enter custom amount"
          >
            <input
              id="enter_amount_custom"
              name="amount"
              type="radio"
              checked={selectedAmountKey === 'custom'}
              value="custom"
              onChange={handleSelectedAmountChange}
            />
            <CustomAmountInputAndCurrencySignWrapper>
              <span style={{ marginRight: '4px' }}>{currencySymbol}</span>
              <CurrencyField
                name="custom_amount"
                placeholder="Enter custom amount"
                aria-label="Custom amount"
                validationRules={{
                  min: {
                    value: 50,
                    message: customAmountValidationMessage,
                  },
                  max: {
                    value: 2500,
                    message: customAmountValidationMessage,
                  },
                  required: {
                    value: selectedAmountKey === 'custom',
                    message: customAmountValidationMessage,
                  },
                }}
                formatValue={formatPriceAsNumber}
                setInputRef={(e: HTMLInputElement) => {
                  customInputRef.current = e
                }}
                onClick={() => setSelectedAmountKey('custom')}
                skipErrorMessage
              />
            </CustomAmountInputAndCurrencySignWrapper>
          </HorizontalHiddenRadioLabel>
          <ErrorMessage name="custom_amount" />
        </InputGroup>

        <InputGroup>
          <TextareaField
            name="message"
            label="Write a heartfelt message"
            validationRules={{
              maxLength: {
                value: MESSAGE_MAX_LENGTH,
                message: `Please enter less than ${MESSAGE_MAX_LENGTH} characters`,
              },
            }}
            formatValue={trimNewLines}
            showLength
            noResize
          />
        </InputGroup>

        {selectedCurrency && (
          <TotalRow>
            <TotalLabel>Total</TotalLabel>
            <TotalAmount>
              {selectedCurrency} {formatPrice(amount)}
            </TotalAmount>
          </TotalRow>
        )}

        {updating && (
          <Overlay>
            <Spinner />
          </Overlay>
        )}

        <InputGroup lessMargin>
          <SubmitButton
            type="submit"
            disabled={updating || isSubmitting}
            rounded
          >
            {updating ? 'Creating voucher...' : 'Continue to payment'}
          </SubmitButton>
        </InputGroup>
      </Form>

      {selectedCurrency && (
        <Disclaimer>
          This gift voucher is valid for escapes booked in {currencyCountry}{' '}
          only. The payment will be processed in {selectedCurrency} and your
          bank will determine the final amount charged, based on current
          exchange rates.
        </Disclaimer>
      )}
    </FormProvider>
  )
}

type ReduxState = {
  vouchers: {
    saving: boolean
  }
}

export default connect(({ vouchers }: ReduxState) => ({
  updating: vouchers.saving,
  initialValues: {
    from_name: '',
    recipient_name: '',
    recipient_email: '',
  },
}))(VoucherCreate)
